import { WindowProxy } from '@sm360/widget-commons-core-services';
import _ from 'lodash';
import { VEHICLE_CONDITION } from 'commons-vehicle/constants/vehicle.js';
import COLANDER_GROUPS from 'commons-general/constants/colanderGroups.js';
import { SD_STATUS } from './constants.js';
import SHIFT_DIGITAL_TAGS from '../../constants/shiftDigitalTags.js';
import SessionStorageService from '../SessionStorageService/SessionStorageService.js';

/* eslint-disable no-underscore-dangle */
class ShiftDigitalService {
    constructor() {
        this.dealerMake = null;
        this.debug = false; // set to true to test Shift Events locally
    }
    getDealerMake() {
        return this.dealerMake || '';
    }
    setDealerMake(dealerMake) {
        this.dealerMake = dealerMake;
    }
    isToyota() {
        return (
            this.getDealerMake().toLowerCase() === 'toyota' ||
            (SessionStorageService.get('dealerMake') &&
                SessionStorageService.get('dealerMake')?.toLowerCase() === 'toyota')
        );
    }
    isLexus() {
        return (
            this.getDealerMake().toLowerCase() === 'lexus' ||
            (SessionStorageService.get('dealerMake') &&
                SessionStorageService.get('dealerMake')?.toLowerCase() === 'lexus')
        );
    }

    hasShiftDigital() {
        if (this.debug) return true;
        return !!(WindowProxy.get() && WindowProxy.get().sd);
    }

    hasDrEvents() {
        // Toyota and Lexus don't want Digital Retailing events
        return !this.isToyota() && !this.isLexus();
    }

    hasTradeInEvents() {
        // For now the tradeIn (non-DR) events are only for Toyota / Lexus
        return this.isToyota() || this.isLexus();
    }

    createFormEvent(leadId, formType) {
        if (this.hasShiftDigital()) {
            WindowProxy.get().sd('dataLayer', {
                leadId,
                formType,
                events: 'formSubmission',
            });
            WindowProxy.get().sd('send');
        }
    }

    // PageView event is meant only to trigger for page View without any data layer properties
    createPageViewEvent() {
        if (this.debug) {
            console.log('Debug Shift Digital: Pageview event');
            return false;
        }
        if (this.hasShiftDigital()) {
            WindowProxy.get().sd('dataLayer', {});
            WindowProxy.get().sd('send', 'pageview');
        }
        return true;
    }
    createEvent({ eventName, datalayer = {} }, isPageView) {
        if (this.debug) {
            console.log('Debug Shift Digital: ', { ...datalayer, events: eventName });
            return false;
        }
        if (this.hasShiftDigital()) {
            WindowProxy.get().sd('dataLayer', { ...datalayer, events: eventName });
            WindowProxy.get().sd('send', isPageView ? 'pageview' : undefined);
        }
        return true;
    }
    createTradeInImpressionEvent() {
        if (this.hasTradeInEvents()) {
            this.createEvent(SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_IMPRESSION);
        }
    }
    createTradeInStartEvent() {
        if (this.hasTradeInEvents()) {
            this.createEvent(SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_START);
        }
    }
    createTradeInVehicleEvent(tradeInYear, tradeInMake, tradeInModel) {
        if (this.hasTradeInEvents()) {
            this.createEvent(
                SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_VEHICLE_SELECTED(tradeInYear, tradeInMake, tradeInModel)
            );
        }
    }
    createTradeInFormStartEvent(tradeInYear, tradeInMake, tradeInModel) {
        if (this.hasTradeInEvents()) {
            this.createEvent(
                SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_FORM_START(tradeInYear, tradeInMake, tradeInModel)
            );
        }
    }
    createTradeInFormSubmissionEvent(tradeInYear, tradeInMake, tradeInModel, leadId) {
        if (this.hasTradeInEvents()) {
            this.createEvent(
                SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_FORM_SUBMISSION(tradeInYear, tradeInMake, tradeInModel, leadId)
            );
        }
    }
    createTradeInAppointmentEvent(tradeInYear, tradeInMake, tradeInModel, leadId) {
        if (this.hasTradeInEvents()) {
            this.createEvent(
                SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_APPOINTMENT(tradeInYear, tradeInMake, tradeInModel, leadId)
            );
        }
    }
    createTradeInCompleteEvent(tradeInYear, tradeInMake, tradeInModel) {
        if (this.hasTradeInEvents()) {
            this.createEvent(SHIFT_DIGITAL_TAGS.TRADEIN_TOYOTA_LEXUS_COMPLETE(tradeInYear, tradeInMake, tradeInModel));
        }
    }

    updateListingEvent(criteria, metadata, vehicleCount, bodyStyles, colanderGroupSlug) {
        let bodyStyle = 'ALL';
        let make = 'ALL';
        let model = 'ALL';
        if (this.hasShiftDigital()) {
            const minYear = criteria?.yearRange?.from || 'ALL';
            const maxYear = criteria?.yearRange?.to || 'ALL';
            const minPrice = criteria?.salePriceRange?.from || 'ALL';
            const maxPrice = criteria?.salePriceRange?.to || 'ALL';
            const searchBar = criteria?.textQuery || '';
            let currentStatus = colanderGroupSlug === COLANDER_GROUPS.NEW ? SD_STATUS.NEW : SD_STATUS.USED;

            const colanderSlug = criteria?.colanderSlug || '';
            const isCertified = colanderSlug.indexOf('certified', 0) !== -1;

            if (isCertified) {
                currentStatus = SD_STATUS.CPO;
            }

            if (criteria?.frameStyleTypes?.length > 0) {
                bodyStyle = this.getCurrentBodyStyles(criteria.frameStyleTypes, bodyStyles)?.join();
            }

            if (criteria?.makeIds?.length > 0) {
                make = this.getCurrentMeta(criteria.makeIds, metadata, 'makeId')?.join();
            }

            if (criteria?.modelIds?.length > 0) {
                model = this.getCurrentMeta(criteria.modelIds, metadata, 'modelId')?.join();
            }

            this.createEvent(
                SHIFT_DIGITAL_TAGS.FILTER_SEARCH(
                    vehicleCount,
                    currentStatus,
                    minYear,
                    maxYear,
                    make,
                    model,
                    minPrice,
                    maxPrice,
                    bodyStyle,
                    searchBar
                )
            );
        }
    }

    updateTypedSearchEvent(criteria) {
        if (this.hasShiftDigital()) {
            const searchBar = criteria?.textQuery || '';
            if (!_.isEmpty(searchBar)) {
                WindowProxy.get().sd('dataLayer', {
                    typedSearch: {
                        typedSearchContent: searchBar,
                    },
                    events: 'typedSearch',
                });
                WindowProxy.get().sd('send');
            }
        }
    }

    updateTradeInEvent(quoteId, eventName, vehicle) {
        if (this.hasTradeInEvents()) {
            return false;
        }
        const dealId = quoteId;
        let vehicleYear = '';
        let vehicleMake = '';
        let vehicleModel = '';
        let vehicleVin = '';
        let vehicleStatus = '';

        if (this.hasShiftDigital()) {
            if (vehicle) {
                vehicleYear = vehicle.year;
                vehicleMake = vehicle.make;
                vehicleModel = vehicle.model;
                vehicleVin = vehicle.vin;
                if (vehicle.vehicleConditionType === 256) {
                    vehicleStatus = 'Used';
                } else if (vehicle.vehicleConditionType === 768) {
                    vehicleStatus = 'CPO';
                } else {
                    vehicleStatus = 'New';
                }
            }

            this.createEvent({
                datalayer: {
                    digRet: {
                        dealId,
                        provider: 'SM360',
                        vehicleYear,
                        vehicleMake,
                        vehicleModel,
                        vehicleVin,
                        vehicleStatus,
                    },
                },
                eventName,
            });
        }

        return true;
    }

    getDrVehicleInfo(dealId, vehicle) {
        let vehicleStatus = '';
        let vehicleYear = '';
        let vehicleMake = '';
        let vehicleModel = '';
        let vehicleVin = '';

        if (vehicle && vehicle._map && vehicle._map._root && vehicle._map._root.nodes) {
            const nodes = vehicle._map._root.nodes;

            const nodesData = _.reduce(
                nodes,
                (acc, value) => {
                    if (_.get(value, 'entry')) {
                        acc.push(value.entry);
                    } else if (_.get(value, 'nodes')) {
                        _.forEach(value.nodes, (nvalue) => {
                            if (_.get(nvalue, 'nodes')) {
                                _.forEach(nvalue.nodes, (nv) => {
                                    acc.push(nv.entry);
                                });
                            } else {
                                acc.push(nvalue.entry);
                            }
                        });
                    }
                    return acc;
                },
                []
            );

            _.forEach(nodesData, (node) => {
                if (node) {
                    const currentColander = node[0];
                    let currentValue = node[1];

                    if (typeof currentValue === 'object') {
                        currentValue = _.get(node[1], 'name');
                    }
                    if (currentColander === 'year') {
                        vehicleYear = currentValue;
                    }
                    if (currentColander === 'make') {
                        vehicleMake = currentValue;
                    }
                    if (currentColander === 'model') {
                        vehicleModel = currentValue;
                    }
                    if (currentColander === 'vin') {
                        vehicleVin = currentValue;
                    }
                    if (currentColander === 'vehicleConditionType') {
                        switch (currentValue) {
                            case 256:
                                vehicleStatus = 'Used';
                                break;
                            case 768:
                                vehicleStatus = 'CPO';
                                break;
                            default:
                                vehicleStatus = 'New';
                        }
                    }
                    if (currentColander === 'vehicleCondition') {
                        switch (currentValue) {
                            case VEHICLE_CONDITION.USED:
                                vehicleStatus = 'Used';
                                break;
                            case VEHICLE_CONDITION.CERTIFIED:
                                vehicleStatus = 'CPO';
                                break;
                            case VEHICLE_CONDITION.DEMO:
                                vehicleStatus = 'Demo';
                                break;
                            case VEHICLE_CONDITION.NEW:
                                vehicleStatus = 'New';
                                break;
                            default:
                                vehicleStatus = currentValue;
                        }
                    }
                }
            });
        }

        return {
            dealId,
            vehicleStatus,
            vehicleYear,
            vehicleMake,
            vehicleModel,
            vehicleVin,
        };
    }

    updateCommonEvent(quoteId, eventName, vehicle) {
        const provider = 'SM360';
        if (this.hasShiftDigital()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent({
                datalayer: {
                    digRet: {
                        ...digRet,
                        provider,
                    },
                },
                eventName,
            });
        }
    }

    formatDrDealType(dealType = '') {
        const type = dealType.toString();
        return type.search(/cash/i) !== -1 ? 'Cash' : _.upperFirst(type);
    }

    updateDrPageView(quoteId, vehicle, vehicleModel) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            let digRet = {};
            if (vehicleModel) {
                digRet = {
                    vehicleYear: vehicleModel.year,
                    vehicleMake: vehicleModel.make,
                    vehicleModel: vehicleModel.model,
                    vehicleStatus: vehicleModel.status || 'New',
                    vehicleVin: vehicleModel.vin,
                    dealId: quoteId,
                };
            } else {
                digRet = this.getDrVehicleInfo(quoteId, vehicle);
            }
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_PAGEVIEW_EVENT({ ...digRet }), true);
        }
    }

    updateDrImpression(quoteId, vehicle, vehicleModel) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            let digRet = {};
            if (vehicleModel) {
                digRet = {
                    vehicleYear: vehicleModel.year,
                    vehicleMake: vehicleModel.make,
                    vehicleModel: vehicleModel.model,
                    vehicleStatus: vehicleModel.status || 'New',
                    vehicleVin: vehicleModel.vin,
                    dealId: quoteId,
                };
            } else {
                digRet = this.getDrVehicleInfo(quoteId, vehicle);
            }
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_IMPRESSION_EVENT({ ...digRet }));
        }
    }

    updateDrInitialClick(quoteId, vehicle) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_INITIAL_CLICK_EVENT(digRet));
        }
    }

    updateDrTradeinShown(quoteId, vehicle) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_TRADEIN_SHOWN_EVENT(digRet));
        }
    }

    updateDrTradeinStart(quoteId, vehicle) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_TRADEIN_START_EVENT(digRet));
        }
    }

    updateDrTradeinFinish(quoteId, vehicle, vehicleModel) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            let digRet = {};
            if (vehicleModel) {
                digRet = {
                    vehicleYear: vehicleModel.year,
                    vehicleMake: vehicleModel.make,
                    vehicleModel: vehicleModel.model,
                    vehicleStatus: 'Used',
                };
            } else {
                digRet = this.getDrVehicleInfo(quoteId, vehicle);
            }
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_TRADEIN_FINISH_EVENT(digRet));
        }
    }

    updateDrCalculatorShown(quoteId, vehicle, dealType, vehicleModel) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            let digRet = {};
            if (vehicleModel) {
                digRet = {
                    vehicleYear: vehicleModel.year,
                    vehicleMake: vehicleModel.make,
                    vehicleModel: vehicleModel.model,
                    vehicleStatus: vehicleModel.status || 'New',
                    vehicleVin: vehicleModel.vin,
                    dealId: quoteId,
                };
            } else {
                digRet = this.getDrVehicleInfo(quoteId, vehicle);
            }
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CALCULATOR_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrCalculatorStart(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CALCULATOR_START_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrCalculatorStartForBuilder(vehicleModel, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = {
                vehicleYear: vehicleModel.year,
                vehicleMake: vehicleModel.make,
                vehicleModel: vehicleModel.model,
                vehicleStatus: 'New',
            };
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CALCULATOR_START_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrCalculatorFinish(quoteId, vehicle, dealType, vehicleModel) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            let digRet = {};
            if (vehicleModel) {
                digRet = {
                    vehicleYear: vehicleModel.year,
                    vehicleMake: vehicleModel.make,
                    vehicleModel: vehicleModel.model,
                    vehicleStatus: vehicleModel.status || 'New',
                    vehicleVin: vehicleModel.vin,
                    dealId: quoteId,
                };
            } else {
                digRet = this.getDrVehicleInfo(quoteId, vehicle);
            }
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CALCULATOR_FINISH_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrCalculatorFinishForBuilder(vehicleModel, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = {
                vehicleYear: vehicleModel.year,
                vehicleMake: vehicleModel.make,
                vehicleModel: vehicleModel.model,
                vehicleStatus: 'New',
            };
            this.createEvent(SHIFT_DIGITAL_TAGS.DR_CALCULATOR_FINISH_EVENT({ ...digRet, dealType }));
        }
    }

    updateDrLeadFormShown(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_LEAD_FORM_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrLeadFormStart(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_LEAD_FORM_START_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrLeadFormFinish(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_LEAD_FORM_FINISH_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrAppointmentShown(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_APPOINTMENT_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrAppointmentStart(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_APPOINTMENT_START_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrAppointmentFinish(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_APPOINTMENT_FINISH_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrPreQShown(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_PRE_Q_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrPreQStart(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_PRE_Q_START_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrPreQFinish(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_PRE_Q_FINISH_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrFiShown(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_FI_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrFiFinish(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_FI_FINISH_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrAccessoriesShown(vehicleModel, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = {
                vehicleYear: vehicleModel.year,
                vehicleMake: vehicleModel.make,
                vehicleModel: vehicleModel.model,
                vehicleStatus: 'New',
            };
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_ACCESSORIES_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrAddToCart(quoteId, vehicle, dealType, addOnName, addOnType, vehicleModel) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            let digRet = {};
            if (vehicleModel) {
                digRet = {
                    vehicleYear: vehicleModel.year,
                    vehicleMake: vehicleModel.make,
                    vehicleModel: vehicleModel.model,
                    vehicleStatus: 'New',
                };
            } else {
                digRet = this.getDrVehicleInfo(quoteId, vehicle);
            }

            this.createEvent(SHIFT_DIGITAL_TAGS.DR_ADD_TO_CART_EVENT({ ...digRet, dealType, addOnName, addOnType }));
        }
    }

    updateDrAccessoriesFinish(vehicleModel, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = {
                vehicleYear: vehicleModel.year,
                vehicleMake: vehicleModel.make,
                vehicleModel: vehicleModel.model,
                vehicleStatus: 'New',
            };
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_ACCESSORIES_FINISH_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrContractShown(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CONTRACT_SHOWN_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrContractStart(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CONTRACT_START_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrContractFinish(quoteId, vehicle, dealType, depositAmt) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_CONTRACT_FINISH_EVENT({
                    ...digRet,
                    dealType: this.formatDrDealType(dealType),
                    depositAmt,
                })
            );
        }
    }

    updateDrOfferAdded(quoteId, vehicle, dealType) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            const digRet = this.getDrVehicleInfo(quoteId, vehicle);
            this.createEvent(
                SHIFT_DIGITAL_TAGS.DR_OFFER_ADDED_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
            );
        }
    }

    updateDrDropSave(quotes) {
        if (this.hasShiftDigital() && this.hasDrEvents()) {
            _.forEach(quotes, (quote) => {
                if (quote.status === 'quote') {
                    const digRet = this.getDrVehicleInfo(quote.id, quote.vehicle);
                    const dealType = quote?.purchaseMethod;
                    this.createEvent(
                        SHIFT_DIGITAL_TAGS.DR_DROP_SAVE_EVENT({ ...digRet, dealType: this.formatDrDealType(dealType) })
                    );
                }
            });
        }
    }

    updateCatalogEvent(filters) {
        let bodyStyle = 'ALL';
        let make = 'ALL';
        let searchBar = '';

        if (this.hasShiftDigital()) {
            if (filters._map && filters._map._root && (filters._map._root.entries || filters._map._root.nodes)) {
                let entries = filters?._map?._root?.entries;

                // If 'entries' field is not available, use 'nodes' field
                if (!entries && filters?._map?._root?.nodes)
                    entries = filters._map._root.nodes.map((node) => node.entry).filter((x) => !!x);

                entries.forEach((entry) => {
                    const currentColander = entry[0];
                    const currentValue = entry[1];
                    if (currentColander === 'preferredMake') {
                        make = currentValue;
                    }
                    if (currentColander === 'modelSearchString') {
                        searchBar = currentValue;
                    }
                    if (currentColander === 'bodyStylesFilterCodes' && currentValue && currentValue.length > 0) {
                        bodyStyle = Array.isArray(currentValue) ? currentValue.join() : currentValue;
                    }
                });
                if (!_.isEmpty(searchBar)) {
                    WindowProxy.get().sd('dataLayer', {
                        typedSearch: {
                            typedSearchContent: searchBar,
                        },
                        events: 'typedSearch',
                    });
                } else {
                    WindowProxy.get().sd('dataLayer', {
                        filterSearch: {
                            make,
                            bodyStyle,
                        },
                        events: 'filterSearch',
                    });
                }
                WindowProxy.get().sd('send');
            }
        }
    }

    getCurrentMeta(currentValues, metadata, metaName) {
        const value = [];
        if (currentValues?.length > 0) {
            metadata.filter((meta) => meta.name === metaName).some((meta) => {
                _.forEach(currentValues, (currentValue) => {
                    const metadatas = meta.metadatas.filter((data) => data.code === currentValue);
                    if (metadatas && !_.isEmpty(metadatas)) {
                        value.push(metadatas[0].label);
                    }
                });
                return false;
            });
        }
        return value;
    }

    getCurrentBodyStyles(currentValues, bodyStyles) {
        const value = [];
        if (currentValues?.length > 0) {
            _.forEach(currentValues, (currentValue) => {
                const bodyStyle = bodyStyles.filter((style) => style.codes.includes(currentValue))?.[0];
                value.push(bodyStyle?.group);
            });
        }
        return value;
    }
}

export default new ShiftDigitalService();
